import {Injectable} from '@angular/core';
import {HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse} from '@angular/common/http';
import {filter, Observable, switchMap, throwError, take, EMPTY, BehaviorSubject} from 'rxjs';
import {AuthService} from '../Services/auth.service';
import {catchError} from 'rxjs/operators';
import {jwtDecode} from 'jwt-decode';
import {BsToastService} from '../Services/bs-toast-service';
import {Router} from '@angular/router';

@Injectable()
export class JwtInterceptorService implements HttpInterceptor {
  currentUser: any;
  refreshTokenSubject$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  tokenIsExpiredSubject$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  refreshTokenIsExpiredSubject$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private isRefreshing = false;
  private tokenExpired = false;
  private refreshExpired = false;

  constructor(private authenticationService: AuthService,
              private notificationService: BsToastService,
              private router: Router) {
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {


    // Do not attach token headers for pre-login auth endpoints only (login, register, forgot, OTP)
    // IMPORTANT: Do NOT exclude report endpoints that happen to contain the word "register"
    // (e.g., /fixed-assets/reports/register). Guard the exception with a non-report check.
    const urlLower = request.url.toLowerCase();
    const isReportRoute = urlLower.includes('/fixed-assets/reports/');
    const isPreAuthEndpoint = (
      urlLower.includes('/login') ||
      urlLower.includes('/register') ||
      urlLower.includes('/forgot-password') ||
      urlLower.includes('otprequest') ||
      urlLower.includes('otprequestverify') ||
      urlLower.includes('/otp/request') ||
      urlLower.includes('/otp/verify')
    );
    if (!isReportRoute && isPreAuthEndpoint) {
      return next.handle(request);
    }

    this.currentUser = JSON.parse(localStorage.getItem('currentUser'));
    this.checkTokenExpired(this.currentUser);

    // if there is no user info nothing else is valid so bail out
    if (!this.currentUser?.accessToken) {
      console.log(`Request to ${request.url} stopped by interceptor.`);
      this.router.navigate(['/login']).then(() => {
      });
      this.notificationService.showWarningToastStayOnScreen('Missing User information present. Please login again.');
      // Optionally, return an empty observable to prevent further processing
      return new Observable<HttpEvent<any>>();

      // Return an observable of an error or empty event
      // return throwError(() => new HttpErrorResponse({ status: 400, error: 'Request blocked by interceptor - no user info' }));

    }
    const refreshExpiring = this.checkRefreshTokenExpired(this.currentUser);

    this.tokenExpired = this.tokenIsExpiredSubject$.value;

    // if (this.currentUser) {
    //   console.log('this endpoint is: ' + request.url);
    if ((this.tokenExpired || refreshExpiring) && !this.isRefreshing) {
      this.isRefreshing = true;
      // console.log('hitting this api endpoint: ' + request.url);
      this.refreshTokenSubject$.next(null);
      return this.authenticationService.refreshToken(this.currentUser).pipe( // Assuming getToken() returns an Observable
        switchMap(token => {
          if (token) {
            this.refreshTokenSubject$.next(token.accessToken);
            const authRequest = this.attachTokenToRequest(request, token.accessToken);
            // console.log('attaching new token to request:' + this.refreshTokenSubject$.value);
            this.isRefreshing = false;
            return next.handle(authRequest);
          } else {
            // console.log('No new token was returned on refresh request - logged out user');
            this.authenticationService.logout();
            return throwError(() => 'Error: No new token was fetched - logged out user');
          }
        }));
    } else {
      // have a token and it and refresh token are still good
      request = this.attachTokenToRequest(request, this.currentUser.accessToken);
      // console.log('not expired - attaching current token to request to: ' + request.url);
      return next.handle(request).pipe(
        catchError(error => {
          if (error instanceof HttpErrorResponse
            && error.status === 401
            && !request.url.includes('/refresh')
            && !request.url.includes('sys_user/me')
            && !request.url.includes('/switch-company')) {
            return this.handle401Error(request, next, 'catchError - pre-expiry handling');
          } else {
            this.refreshExpired = false;
            return throwError(() => error);
          }
        })
      );
    }
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler, caller = ''): Observable<HttpEvent<any>> {
    // console.log('handle 401 called by: ' + caller + ' with this endpoint: ' + request.url);
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject$.next(null);
      return this.authenticationService.refreshToken().pipe(
        switchMap((token: any) => {
          this.isRefreshing = false;
          const access = token?.accessToken || token?.access;
          if (!access) {
            this.authenticationService.logout();
            return throwError(() => 'Error: No new token was fetched - logged out user');
          }
          this.refreshTokenSubject$.next(access);
          return next.handle(this.attachTokenToRequest(request, access));
        }),
        catchError((err) => {
          this.isRefreshing = false;
          this.authenticationService.logout();
          return throwError(() => err);
        })
      );
    } else {
      return this.refreshTokenSubject$.pipe(
        filter(token => token != null),
        take(1),
        switchMap(accessToken => {
          return next.handle(this.attachTokenToRequest(request, accessToken));
        })
      );
    }
  }

  /**
   * Attaches a JWT token to the authorization header of the provided HTTP request.
   *
   * @param {HttpRequest<any>} request - The HTTP request to which the token will be attached.
   * @param {string} token - The JWT token to be attached to the request.
   * @return {HttpRequest<any>} - A cloned HTTP request with the token attached to its headers.
   */
  private attachTokenToRequest(request: HttpRequest<any>, token: string): HttpRequest<any> {
    // console.log(`token in jwt: ${token}`);
    // console.log('attaching existing token to request');
    if (request.method === 'POST' && request.body instanceof FormData) {
      // This is a POST request with FormData (multipart) - do not use content tyee header
      return request.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`,
          Accept: '*/*'
        }
      });
    } else {
      // This is a POST request with JSON data
      return request.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          Accept: '*/*'
        }
      });
    }


  }

  /**
   * Checks whether the current token is expired or nearing its end of life.
   * Evaluates the token expiration time and determines if a refresh action is needed.
   *
   * @return {Observable<boolean>} An observable emitting `true` if the token is near expiration
   * or expired, and `false` otherwise.
   */
  private checkTokenExpired(tokenInfo): boolean {
    // Null-safety: if no token info or accessToken, consider not expired
    if (!tokenInfo || !tokenInfo.accessToken) {
      this.tokenIsExpiredSubject$.next(false);
      return false;
    }
    try {
      let retVal = false;
      const decodedToken: any = jwtDecode(tokenInfo.accessToken);
      const currentTime = Math.floor(new Date().getTime() / 1000);
      const expClaim = Number(decodedToken?.exp);
      if (!expClaim || Number.isNaN(expClaim)) {
        this.tokenIsExpiredSubject$.next(false);
        return false;
      }
      // Normalize exp to seconds if backend provided milliseconds
      const expSeconds = expClaim > 1e12 ? Math.floor(expClaim / 1000) : expClaim;
      const tokenExpiryInMins = (expSeconds - currentTime) / 60;
      // Token is already dead — force logout immediately rather than sending a doomed request.
      // This handles the case where the app was asleep and the timer never fired.
      if (tokenExpiryInMins <= 0) {
        this.tokenIsExpiredSubject$.next(false);
        this.authenticationService.logout(true, 'Your session has expired. Please log in again.');
        return false;
      }
      // Token is near EOL — trigger a proactive refresh before it dies.
      if (tokenExpiryInMins < 2) {
        this.tokenIsExpiredSubject$.next(true);
        retVal = true;
      } else {
        this.tokenIsExpiredSubject$.next(false);
        retVal = false;
      }
      return retVal;
    } catch (e) {
      // Any parsing/decoding error means we cannot compute expiry; avoid crashing
      this.tokenIsExpiredSubject$.next(false);
      return false;
    }
  }

  private checkRefreshTokenExpired(tokenInfo): boolean {
    // Null-safety for pre-login and incomplete states (e.g., during OTP flow before verify):
    if (!tokenInfo || !tokenInfo.refreshTokenExpiration) {
      return false;
    }
    let retVal = false;
    const currentTime = Math.floor(new Date().getTime() / 1000);
    const expMs = new Date(tokenInfo.refreshTokenExpiration).getTime();
    if (Number.isNaN(expMs)) {
      return false;
    }
    const tokenExpiry = Math.floor(expMs / 1000);
    const tokenExpiryInMins = (tokenExpiry - currentTime) / 60;
    // if token is near EOL then fetch but if dead ignore and let 401 hit.
    if (tokenExpiryInMins < 2) {
      retVal = true;
    } else {
      retVal = false;
    }
    return retVal;
  }
}
